// Smart trip-time suggestion.
//
// Given a target day + location, suggest the best start time for a fishing
// trip by combining the classic dawn/dusk bite windows with the solunar major
// periods (moon overhead / underfoot), using the same SunCalc math the
// dashboard's Solunar indicator relies on. Pure function — safe to call
// outside React. Never throws; falls back to a dawn default without GPS.

import SunCalc from "suncalc";

const MIN = 60 * 1000;
const HOUR = 60 * MIN;

function dayStart(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

// The two solunar major period centers for a day: moon at its highest
// (overhead) and lowest (underfoot) altitude. Sampled every 5 minutes.
function solunarCenters(date, lat, lng) {
  const start = dayStart(date);
  let hi = null;
  let lo = null;
  for (let m = 0; m <= 24 * 60; m += 5) {
    const t = new Date(start.getTime() + m * MIN);
    const { altitude } = SunCalc.getMoonPosition(t, lat, lng);
    if (!hi || altitude > hi.altitude) hi = { t, altitude };
    if (!lo || altitude < lo.altitude) lo = { t, altitude };
  }
  if (!hi || !lo) return [];
  return [
    { type: "overhead", center: hi.t },
    { type: "underfoot", center: lo.t },
  ];
}

function fmt(date) {
  return new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(date);
}

/**
 * Suggest the best start time on `baseDate` for the given location.
 * @param {Date|number} baseDate - any moment on the target day
 * @param {{lat:number,lng:number}} location
 * @returns {{ at: Date, reason: string }}
 */
export function suggestTripTime(baseDate, location) {
  const lat = Number(location?.lat);
  const lng = Number(location?.lng);
  const day = dayStart(baseDate);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    const at = new Date(day.getTime() + 6 * HOUR); // 6:00 AM default
    return { at, reason: "Dawn window (enable GPS for solunar-tuned times)" };
  }

  try {
    // SunCalc wants a daytime anchor to resolve that calendar day's events.
    const noon = new Date(day.getTime() + 12 * HOUR);
    const { sunrise, sunset } = SunCalc.getTimes(noon, lat, lng);
    const majors = solunarCenters(day, lat, lng);

    // Two classic anchors: just before sunrise and heading into sunset.
    const anchors = [
      { at: new Date((sunrise?.getTime() || day.getTime() + 6 * HOUR) - 30 * MIN), tod: "sunrise" },
      { at: new Date((sunset?.getTime() || day.getTime() + 19 * HOUR) - 60 * MIN), tod: "sunset" },
    ];

    let best = null;
    for (const a of anchors) {
      let nearest = null;
      let nearestDist = Infinity;
      for (const m of majors) {
        const dist = Math.abs(m.center.getTime() - a.at.getTime());
        if (dist < nearestDist) {
          nearestDist = dist;
          nearest = m;
        }
      }
      const overlaps = nearestDist <= 2 * HOUR;
      // Prefer anchors whose bite window overlaps a solunar major; among those,
      // the tighter the overlap the better. Morning wins ties (earlier anchor).
      const score = overlaps ? 2 * HOUR - nearestDist : -nearestDist;
      if (!best || score > best.score) best = { ...a, nearest, nearestDist, overlaps, score };
    }

    const todLabel = best.tod === "sunrise" ? "sunrise" : "sunset";
    let reason;
    if (best.overlaps && best.nearest) {
      reason = `Solunar major (${best.nearest.type}) near ${todLabel} — peak bite around ${fmt(best.nearest.center)}`;
    } else {
      reason = `Prime ${todLabel} bite window`;
    }
    return { at: best.at, reason };
  } catch (err) {
    const at = new Date(day.getTime() + 6 * HOUR);
    return { at, reason: "Dawn window" };
  }
}
